import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../../stores/gameStore';
import { useConversationStore } from '../../stores/conversationStore';
import { useCAGStore } from '../../stores/cagStore';
import { useAuthStore } from '../../stores/authStore';
import { useCAGAvailability } from '../../hooks/useCAGAvailability';
import { getApiUrl } from '../../lib/api';

interface Game {
  id: string;  // Changed from game_id to id to match API response
  name: string;
  description?: string;
  complexity?: string;
  rule_count: number;
  ai_tags?: string[];
}

export const GameSelector: React.FC = () => {
  const navigate = useNavigate();
  const { selectedGame, selectGame } = useGameStore();
  const { createNewConversation } = useConversationStore();
  const { createConversation } = useCAGStore();
  const { user, token } = useAuthStore();
  const { isCAGAvailable, isChecking } = useCAGAvailability();
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [useCAG, setUseCAG] = useState<boolean>(true); // Default to using CAG

  const { data: gamesData, isLoading, error } = useQuery({
    queryKey: ['games'],
    queryFn: async () => {
      const url = getApiUrl('/api/games/');
      console.log('🎮 Fetching games from:', url);
      
      const response = await fetch(url);
      console.log('📡 Games API response:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Games API error:', errorText);
        throw new Error(`Failed to load games: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Games data received:', data);
      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: 1000,
  });

  const games = gamesData?.games || [];

  // Handle game selection and navigation to chat
  const handleGameSelect = async (game: Game) => {
    try {
      // Select the game in the store
      selectGame(game);
      
      if (useCAG && user && isCAGAvailable === true) {
        // Use CAG (Cache-Augmented Generation) for optimized conversations
        console.log('🚀 Creating CAG conversation for:', game.name);
        console.log('🚀 User ID:', user.id, 'Game ID:', game.id);
        console.log('🚀 User object:', user);
        console.log('🚀 Full game object:', game);
        console.log('🚀 Token present:', !!token);
        
        if (!user.id) {
          console.error('❌ User ID is missing!', user);
          throw new Error('User ID is required but missing');
        }
        
        if (!game.id) {
          console.error('❌ Game ID is missing!', game);
          throw new Error('Game ID is required but missing');
        }
        
        const gameId = game.id;
        console.log('🚀 About to call createConversation with:', { userId: user.id, gameId, hasToken: !!token });
        await createConversation(user.id, gameId, token || undefined);
        
        // Navigate to CAG chat
        const cagChatUrl = `/cag-chat/${gameId}`;
        console.log('🚀 Navigating to CAG chat:', cagChatUrl);
        navigate(cagChatUrl);
      } else {
        // Fallback to legacy conversation system
        console.log('📚 Creating legacy conversation for:', game.name);
        const gameId = game.id;
        const conversation = await createNewConversation(gameId, game.name);
        
        // Navigate to legacy chat with conversation ID
        navigate(`/chat/${gameId}/${conversation.id}`);
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
      
      // Check if CAG is not available and fall back to legacy mode
      if (error instanceof Error && error.message === 'CAG_NOT_AVAILABLE') {
        console.log('📚 CAG not available on backend, falling back to legacy conversation');
        try {
          const conversation = await createNewConversation(game.id, game.name);
          navigate(`/chat/${game.id}/${conversation.id}`);
          return;
        } catch (legacyError) {
          console.error('Legacy conversation also failed:', legacyError);
        }
      }
      
      // Final fallback to simple chat navigation
      selectGame(game);
      const gameId = game.id;
      navigate(`/chat/${gameId}`);
    }
  };

  // Filter games by category (using complexity as category for now)
  const filteredGames = filterCategory 
    ? games.filter((game: Game) => game.complexity?.toLowerCase() === filterCategory.toLowerCase())
    : games;

  // Get unique categories with proper typing (using complexity as category for now)
  const categories: string[] = Array.from(new Set(games.map((game: Game) => game.complexity).filter(Boolean)));

  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
        <div style={{ 
          color: 'red', 
          padding: '20px', 
          border: '1px solid red', 
          borderRadius: '8px',
          backgroundColor: '#fee' 
        }}>
          <h3 style={{ margin: '0 0 10px 0' }}>❌ Failed to load games</h3>
          <p style={{ margin: '0 0 10px 0' }}>
            <strong>Error:</strong> {(error as Error).message}
          </p>
          <details style={{ marginTop: '10px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
              Debugging Info
            </summary>
            <div style={{ marginTop: '10px', fontSize: '14px', fontFamily: 'monospace' }}>
              <p><strong>API URL:</strong> {getApiUrl('/api/games')}</p>
              <p><strong>Environment:</strong> {process.env.REACT_APP_ENV}</p>
              <p><strong>Base URL:</strong> {process.env.REACT_APP_API_URL}</p>
            </div>
          </details>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginTop: '15px',
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry Loading Games
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <h2>Select a Game</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              data-testid="game-skeleton"
              style={{ 
                height: '200px', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '8px',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <h2>Select a Game</h2>
        <div style={{ 
          padding: '20px', 
          border: '1px solid #ccc', 
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <p style={{ fontSize: '18px', color: '#666', margin: '0 0 15px 0' }}>
            📚 No games available in the library yet.
          </p>
          <details style={{ marginTop: '15px', textAlign: 'left' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#555' }}>
              🔧 Debug Information
            </summary>
            <div style={{ marginTop: '10px', fontSize: '14px', fontFamily: 'monospace', color: '#666' }}>
              <p><strong>API Response:</strong> {JSON.stringify(gamesData, null, 2)}</p>
              <p><strong>Games Array:</strong> {JSON.stringify(games)}</p>
              <p><strong>API URL:</strong> {getApiUrl('/api/games')}</p>
            </div>
          </details>
          <p style={{ fontSize: '14px', color: '#888', marginTop: '15px' }}>
            Games need to be uploaded to the database first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2>Select a Game</h2>
      
      {/* CAG Mode Toggle */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '15px', 
        backgroundColor: useCAG && isCAGAvailable === true ? '#e8f5e8' : 
                         isCAGAvailable === false ? '#fff3cd' : 
                         isCAGAvailable === 'error' ? '#f8d7da' : '#f5f5f5', 
        borderRadius: '8px',
        border: useCAG && isCAGAvailable === true ? '1px solid #28a745' : 
                isCAGAvailable === false ? '1px solid #ffc107' : 
                isCAGAvailable === 'error' ? '1px solid #dc3545' : '1px solid #ccc'
      }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: isCAGAvailable === true ? 'pointer' : 'not-allowed' }}>
          <input
            type="checkbox"
            checked={useCAG && isCAGAvailable === true}
            onChange={(e) => setUseCAG(e.target.checked)}
            disabled={isCAGAvailable !== true}
            style={{ marginRight: '10px', transform: 'scale(1.2)' }}
          />
          <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
            {isChecking ? '🔄 Checking CAG...' : 
             isCAGAvailable === false ? '📚 Legacy Mode (CAG Unavailable)' :
             isCAGAvailable === 'error' ? '📚 Legacy Mode (CAG Error)' :
             useCAG ? '🚀 CAG Mode (Recommended)' : '📚 Legacy Mode'}
          </span>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {isChecking ? 'Checking backend CAG support...' :
             isCAGAvailable === false ? 'CAG endpoints not available on backend' :
             isCAGAvailable === 'error' ? 'CAG endpoints experiencing issues (500 error)' :
             useCAG 
              ? 'Optimized conversations with smart context management' 
              : 'Traditional chat with full rule context'}
          </span>
        </label>
        {!user && useCAG && isCAGAvailable === true && (
          <p style={{ margin: '5px 0 0 30px', fontSize: '12px', color: '#dc3545' }}>
            ⚠️ Login required for CAG mode
          </p>
        )}
        {isCAGAvailable === false && (
          <p style={{ margin: '5px 0 0 30px', fontSize: '12px', color: '#856404' }}>
            💡 CAG mode will be available after backend deployment
          </p>
        )}
        {isCAGAvailable === 'error' && (
          <p style={{ margin: '5px 0 0 30px', fontSize: '12px', color: '#721c24' }}>
            ⚠️ CAG endpoints deployed but experiencing server errors (check backend logs)
          </p>
        )}
      </div>
      
      {selectedGame && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          backgroundColor: '#e6f3ff', 
          borderRadius: '8px' 
        }}>
          <p style={{ fontWeight: 'bold', margin: '0 0 5px 0' }}>Currently Selected:</p>
          <p style={{ fontSize: '18px', margin: '0' }}>{selectedGame.name}</p>
        </div>
      )}

      {/* Category Filters */}
      {categories.length > 1 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Filter by Category:</p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setFilterCategory(null)}
              style={{
                padding: '5px 12px',
                border: '1px solid #ccc',
                backgroundColor: filterCategory === null ? '#0066cc' : 'white',
                color: filterCategory === null ? 'white' : 'black',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              All
            </button>
            {categories.map((category: string) => (
              <button
                key={category}
                onClick={() => setFilterCategory(category)}
                style={{
                  padding: '5px 12px',
                  border: '1px solid #ccc',
                  backgroundColor: filterCategory === category ? '#0066cc' : 'white',
                  color: filterCategory === category ? 'white' : 'black',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: '20px' 
      }}>
        {filteredGames.map((game: Game) => (
          <div
            key={game.id}
            onClick={() => handleGameSelect(game)}
            style={{
              padding: '20px',
              backgroundColor: 'white',
              border: selectedGame?.id === game.id ? '2px solid #0066cc' : '1px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            title={`Click to start chatting about ${game.name} rules`}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
              <h3 role="heading" style={{ margin: '0', fontSize: '20px' }}>
                {game.name}
              </h3>
              <span style={{ 
                padding: '2px 8px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                borderRadius: '12px', 
                fontSize: '12px' 
              }}>
                {game.complexity || 'Unknown'}
              </span>
            </div>
            
            <p style={{ 
              color: '#666', 
              margin: '10px 0', 
              lineHeight: '1.4',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical'
            }}>
              {game.description}
            </p>

            <p style={{ fontSize: '14px', color: '#888', margin: '10px 0' }}>
              {game.rule_count} rules available
            </p>
            
            <button 
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: selectedGame?.id === game.id ? '#0066cc' : '#f8f9fa',
                color: selectedGame?.id === game.id ? 'white' : '#333',
                border: selectedGame?.id === game.id ? 'none' : '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {selectedGame?.id === game.id ? '✅ Selected' : '🚀 Start Chatting'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};